import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link as HeadLink, Meta, Title } from 'react-head'
import cn from 'classnames'
import DefaultLayout from '../../components/DefaultLayout'
import Footer from '../../components/Footer/Footer'
import PremiumButton from '../../components/PremiumButton/PremiumButton'
import { languages } from '../../locale'
import useLocale from '../../hooks/useLocale'
import Header from '../../components/Header/Header'
import Review from '../../components/Review'
import ErrorPage from '../ErrorPage'
import withSSR from '../../components/withSSR/withSSR'
import convert from 'htmr'
import LazyImage from '../../components/LazyImage/LazyImage'
import Section, { getRecords } from './Section'
import check from 'media/check.svg'
import useBreakpoints from '../../hooks/useBreakpoints'
import QuoteSection from './QuoteSection'
import PlanListing from './PlanListing'
import Fold from '../../components/Fold'
import { OnPageNav, OnPageNavSection } from './OnPageNav'
import RecipeViewer from './RecipeViewer'
import { runQuery } from 'js/dato'
import { query } from 'js/pages/FeaturesPage/featuresPageQuery'
import datoLocales from 'js/datoLocales'
import Spinner from 'js/components/Spinner/Spinner'

function FeaturesPage({ data, isLoading, error }) {
  const { language } = useLocale()
  const { LG } = useBreakpoints()
  const [recipe, setRecipe] = useState(null)

  if (isLoading) {
    return (
      <DefaultLayout showInstallBanner={false}>
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner width="50" color="green" />
        </div>
      </DefaultLayout>
    )
  }
  if (error) return <ErrorPage error={error} />

  const {
    mainHeading,
    pageIntro,
    foodTrackingSection,
    reviews,
    reviewImage,
    mealPlansSection,
    mealPlans,
    supplementaryCtaText,
    dietsSection,
    diets,
    recipesSection,
    sampleRecipesHeading,
    sampleRecipes,
    summarySection,
    pageBottomIllustration,
    pageBottomHeading,
    ctaUsps,
  } = data

  return (
    <div className="bg-BgSub">
      <DefaultLayout>
        <Title>Features – How our health app works - Lifesum</Title>
        <Meta
          name="description"
          content="Choose the best Meal Plan or Diet for you. Track calories, track macros and track habits with our app. Here’s an overview of all our features."
        />
        <HeadLink
          rel="canonical"
          href={
            language === 'en'
              ? `https://lifesum.com/features/`
              : `https://lifesum.com/${language}/features/`
          }
        />
        <HeadLink
          rel="alternate"
          href="https://lifesum.com/features/"
          // @ts-ignore
          hreflang="x-default"
        />
        {languages.map((locale) => (
          <HeadLink
            rel="alternate"
            href={
              locale === 'en'
                ? `https://lifesum.com/features/`
                : `https://lifesum.com/${locale}/features/`
            }
            // @ts-ignore
            hreflang={locale}
            key={locale}
          />
        ))}
        <div className="bg-Bg">
          <div className="bg-BgSub">
            <Header inverted={false} sticky={false} />
            <div className="pb-56 pt-24 text-center md:pb-72 md:pt-48 lg:pb-96 xl:pb-128">
              <div className="o-width-limiter">
                <h1 className="type-huge-fat mb-8 text-green xl:pt-40">
                  {mainHeading}
                </h1>
                <div
                  className={
                    'demibolden-children mx-auto max-w-576 leading-body md:type-20 lg:max-w-768 lg:type-24 xxl:type-28'
                  }
                >
                  {convert(pageIntro)}
                </div>
              </div>
            </div>
          </div>
          <div
            className="mx-auto -translate-y-1/2 transform"
            style={{ maxWidth: 300 }}
          >
            <PremiumButton
              GALabel={'Features curve button'}
              marginless={true}
              countdown={false}
              signUpCopyBlock={true}
              absoluteFromPrice={true}
            />
          </div>
        </div>
        <div className="bg-Bg pt-40">
          <OnPageNav>
            <OnPageNavSection
              id="food-tracking"
              heading={
                getRecords(
                  foodTrackingSection,
                  'FeatureSectionCopyRecord',
                  true,
                ).heading
              }
            >
              <Section section={foodTrackingSection} />
              <Fold />
              <ReviewsSection reviews={reviews} image={reviewImage} />
            </OnPageNavSection>
            <OnPageNavSection
              id="meal-plans"
              heading={
                getRecords(mealPlansSection, 'FeatureSectionCopyRecord', true)
                  .heading
              }
            >
              <Section section={mealPlansSection} />
              <PlanListing plans={mealPlans.plans} />
              <QuoteSection supplementaryCtaText={supplementaryCtaText} />
            </OnPageNavSection>
            <OnPageNavSection
              id="diets"
              heading={
                getRecords(dietsSection, 'FeatureSectionCopyRecord', true)
                  .heading
              }
            >
              <Section section={dietsSection} />
              <PlanListing plans={diets.plans} />
            </OnPageNavSection>
            <OnPageNavSection
              id="recipes"
              heading={
                getRecords(recipesSection, 'FeatureSectionCopyRecord', true)
                  .heading
              }
            >
              <Section section={recipesSection} />
              <SampleRecipesSection
                heading={sampleRecipesHeading}
                recipes={sampleRecipes}
                setRecipe={setRecipe}
              />
            </OnPageNavSection>
            <OnPageNavSection
              id="summary"
              heading={
                getRecords(summarySection, 'FeatureSectionCopyRecord', true)
                  .heading
              }
            >
              <Section section={summarySection} />
              <div
                className="text-center"
                style={{
                  background: `linear-gradient(var(--color--Bg) 35%, var(--color--BgSub) 35%)`,
                }}
              >
                <LazyImage
                  className="mx-auto mb-12 md:mb-24"
                  src={pageBottomIllustration.url}
                  width={pageBottomIllustration.width}
                  height={pageBottomIllustration.height}
                  alt=""
                />
              </div>
              <div className="bg-BgSub">
                <div className="o-width-limiter text-center">
                  <p className="type-med-plus-fat mx-auto max-w-576 leading-default xxl:max-w-768">
                    {pageBottomHeading}
                  </p>
                  <PremiumButton GALabel={'Features middle of page'} />
                  <ul className="mx-16 mt-6 inline-block text-left md:mx-auto md:mt-32 md:block md:text-center xl:mt-40">
                    {ctaUsps.map(({ text }, i) => (
                      <li
                        key={`ctausp-${i}`}
                        className="mx-12 pl-30 leading-body md:inline-block lg:pl-32"
                        style={{
                          background: `url(${check}) 0 4px no-repeat`,
                          backgroundSize: LG ? '22px' : '19px',
                        }}
                      >
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </OnPageNavSection>
          </OnPageNav>
        </div>
        <Footer />
        {recipe && (
          <RecipeViewer recipe={recipe} onClose={() => setRecipe(null)} />
        )}
      </DefaultLayout>
    </div>
  )
}

FeaturesPage.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
}

function ReviewsSection({ reviews, image }) {
  return (
    <div className="o-width-limiter o-width-limiter--narrow lg:-mt-40 xl:-mt-56">
      <div className="flex flex-col-reverse content-center lg:flex-row">
        <div className="my-auto lg:pr-40 xl:pr-64">
          {reviews.map((review, i) => {
            return (
              <div className="lg:mt-48 xl:mt-64" key={'review' + i}>
                <Review big rating={review.rating} text={review.reviewText} />
              </div>
            )
          })}
        </div>
        <div className="m-auto mb-32 w-2/3 shrink-0 lg:mb-auto lg:w-1/2">
          <LazyImage
            className="rounded-16"
            src={image.url}
            width={image.width}
            height={image.height}
            alt=""
          />
        </div>
      </div>
    </div>
  )
}

ReviewsSection.propTypes = {
  reviews: PropTypes.array,
  image: PropTypes.object,
}

function SampleRecipesSection({ heading, recipes, setRecipe }) {
  return (
    <div className="o-width-limiter mb-56 text-center xl:mb-72">
      <h3 className="type-med-plus-fat mb-24 lg:mb-32">{heading}</h3>
      <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:gap-16 xl:grid-cols-6 xl:gap-6">
        {recipes.map((recipe) => {
          return (
            <li key={recipe.recipeName} className="flex items-stretch">
              <button
                onClick={() => setRecipe(recipe)}
                className={cn(
                  'flex flex-col justify-start',
                  'w-full',
                  'cursor-pointer',
                  'rounded-8 shadow-elevated',
                  'overflow-hidden',
                  'transition-fast transform transition hover:-translate-y-1',
                )}
              >
                <LazyImage
                  src={`${recipe.image.url}&w=200&h=180`}
                  width={200}
                  height={180}
                  alt=""
                  className="w-full"
                />
                <h4 className="type-small w-full px-16 py-12">
                  {recipe.recipeName}
                </h4>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

SampleRecipesSection.propTypes = {
  heading: PropTypes.string,
  recipes: PropTypes.array,
  setRecipe: PropTypes.func,
}

FeaturesPage.getInitialProps = ({ isPreview, language }) => {
  const locale = datoLocales[language]
  return runQuery(query, { locale }, isPreview).then(
    ({ featuresPage }) => featuresPage,
  )
}

export default withSSR(FeaturesPage)
